import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type FontSize = 'small' | 'medium' | 'large';

interface FontSizeContextType {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  decreaseFont: () => void;
  resetFont: () => void;
  increaseFont: () => void;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};

interface FontSizeProviderProps {
  children: ReactNode;
}

const fontSizeMap: Record<FontSize, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
};

export const FontSizeProvider: React.FC<FontSizeProviderProps> = ({ children }) => {
  const [fontSize, setFontSizeState] = useState<FontSize>('medium');

  useEffect(() => {
    const savedFontSize = localStorage.getItem('app-font-size') as FontSize;
    if (savedFontSize && ['small', 'medium', 'large'].includes(savedFontSize)) {
      setFontSizeState(savedFontSize);
      document.documentElement.style.fontSize = fontSizeMap[savedFontSize];
    }
  }, []);

  const setFontSize = (size: FontSize) => {
    setFontSizeState(size);
    document.documentElement.style.fontSize = fontSizeMap[size];
    localStorage.setItem('app-font-size', size);
  };

  const decreaseFont = () => {
    if (fontSize === 'large') setFontSize('medium');
    else if (fontSize === 'medium') setFontSize('small');
  };

  const resetFont = () => {
    setFontSize('medium');
  };

  const increaseFont = () => {
    if (fontSize === 'small') setFontSize('medium');
    else if (fontSize === 'medium') setFontSize('large');
  };

  return (
    <FontSizeContext.Provider value={{ fontSize, setFontSize, decreaseFont, resetFont, increaseFont }}>
      {children}
    </FontSizeContext.Provider>
  );
};
